import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import RelevantDateCell from '../../../components/service/relevantDateCell/relevantDateCell.component';
import { formatQuantity, getContrastColor } from '../../../utils';
import { JOB_STATUS_COLOR_MAP } from '../../../consts';

interface JobTaskChipItem {
  label: string;
  color?: string | null;
}

interface JobTaskUsedPart {
  part_id: number;
  name: string;
  code: string | null;
  unit: string;
  quantity: number | string;
}

const ChipsCell = ({ items }: { items: JobTaskChipItem[] }) => {
  if (items.length === 0) {
    return <>-</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        width: '100%',
        height: '100%',
        minHeight: 52,
      }}
    >
      {items.map((item, index) => (
        <Chip
          key={index}
          label={item.label}
          size="small"
          sx={
            item.color
              ? {
                  bgcolor: item.color,
                  color: getContrastColor(item.color),
                  '& .MuiChip-label': { color: getContrastColor(item.color) },
                }
              : {
                  bgcolor: 'rgba(128, 128, 128, 0.25)',
                  color: 'text.secondary',
                }
          }
        />
      ))}
    </Box>
  );
};

/**
 * Columns for the "service history" tables - the list of JobTasks done on one
 * piece of equipment, shown on the equipment view, the equipment statistics
 * page and the job view. Every row is one visit to that machine: which job it
 * belonged to, when it mattered, what was wrong, what was done and which parts
 * it consumed. Clicking a row opens the job.
 *
 * Column `field`s are the backend ordering paths (`job__number` sorts naturally
 * over Job's number_sort column, see common.filters.NaturalOrderingFilter), so
 * the grid's sort model can be sent to the API as-is.
 */
export const useJobTaskServiceHistoryColumns = (): GridColDef[] => {
  const { t } = useTranslation();

  return useMemo(() => [
    {
      field: "job__number",
      filterable: false,
      headerName: t("Number"),
      minWidth: 140,
      valueGetter: (_value, row) => row.job?.number || "-",
    },
    {
      field: "job__status",
      filterable: false,
      headerName: t("Status"),
      minWidth: 140,
      valueGetter: (_value, row) => row.job?.status_display || "-",
      renderCell: (params) => {
        const status = params.row.job?.status;
        const label = params.row.job?.status_display || "-";
        return (
          <Chip label={label} color={JOB_STATUS_COLOR_MAP[status] ?? "default"} size="small" />
        );
      },
    },
    {
      // Which date this is depends on the job's status, so it cannot be sorted
      // as a single column - same as on the jobs list.
      field: "timeline_date",
      filterable: false,
      headerName: t("Relevant date"),
      sortable: false,
      minWidth: 160,
      valueGetter: (_value, row) => row.job?.timeline_date || null,
      renderCell: (params) => (
        <RelevantDateCell value={params.row.job?.timeline_date} status={params.row.job?.status} />
      ),
    },
    {
      field: "issues",
      filterable: false,
      headerName: t("Issues / Services"),
      sortable: false,
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const issues = Array.isArray(params.row.issues) ? params.row.issues : [];
        const services = Array.isArray(params.row.services) ? params.row.services : [];
        const items: JobTaskChipItem[] = [
          ...issues.map((issue: { code?: string | null; title?: string; color?: string | null }) => ({
            label: issue.code ? `${issue.title || "-"} (${issue.code})` : issue.title || "-",
            color: issue.color,
          })),
          ...services.map((service: { title?: string; color?: string | null }) => ({
            label: service.title || "-",
            color: service.color,
          })),
        ];

        return <ChipsCell items={items} />;
      },
    },
    {
      field: "used_parts",
      filterable: false,
      headerName: t("Used parts"),
      sortable: false,
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const usedParts: JobTaskUsedPart[] = Array.isArray(params.row.used_parts)
          ? params.row.used_parts
          : [];
        const items = usedParts.map((usedPart) => ({
          label: `${usedPart.name} × ${`${formatQuantity(usedPart.quantity)} ${usedPart.unit ?? ""}`.trim()}`,
          color: null,
        }));

        return <ChipsCell items={items} />;
      },
    },
    {
      field: "job_assigned_to_names",
      filterable: false,
      headerName: t("Assigned To"),
      sortable: false,
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) => {
        const names = row.job?.assigned_to_names;
        if (Array.isArray(names) && names.length > 0) {
          return names.filter(Boolean).join(", ");
        }
        return "-";
      },
      renderCell: (params) => {
        const names = params.row.job?.assigned_to_names;
        const filtered = Array.isArray(names) ? names.filter(Boolean) : [];
        if (filtered.length === 0) {
          return "-";
        }
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              alignItems: "center",
              width: "100%",
              height: "100%",
              minHeight: 52,
            }}
          >
            {filtered.map((name: string, index: number) => (
              <Chip key={`${name}-${index}`} label={name} size="small" variant="outlined" />
            ))}
          </Box>
        );
      },
    },
  ], [t]);
};
